import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { supabase } from '../lib/supabase';
import {
  loadNotifications,
  markAllRead,
  markRead,
  type Notification,
} from '../lib/notifications';

export function NotificationBell() {
  const { userId } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!userId) return;
    loadNotifications(userId).then(setItems);
    const ch = supabase
      .channel(`notif-${userId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
        () => loadNotifications(userId).then(setItems),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [userId]);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const unread = items.filter((i) => !i.read).length;

  async function openNotif(n: Notification) {
    if (!n.read) {
      await markRead(n.id);
      setItems((xs) => xs.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    setOpen(false);
    if (n.productId) nav(`/produto/${n.productId}`);
  }

  async function allRead() {
    if (!userId) return;
    await markAllRead(userId);
    setItems((xs) => xs.map((x) => ({ ...x, read: true })));
  }

  return (
    <div className="notif" ref={ref}>
      <button className="notif-btn" onClick={() => setOpen((o) => !o)} title="Notificações">
        🔔
        {unread > 0 && <span className="notif-badge">{unread > 9 ? '9+' : unread}</span>}
      </button>
      {open && (
        <div className="notif-panel card">
          <div className="notif-head">
            <b>Notificações</b>
            {unread > 0 && (
              <button className="linklike" onClick={allRead}>marcar todas como lidas</button>
            )}
          </div>
          {items.length === 0 ? (
            <p className="muted" style={{ padding: '10px 12px', margin: 0 }}>Nada por aqui.</p>
          ) : (
            <div className="notif-list">
              {items.map((n) => (
                <button
                  key={n.id}
                  className={`notif-item ${n.read ? '' : 'unread'}`}
                  onClick={() => openNotif(n)}
                >
                  <div className="notif-text">{n.text}</div>
                  <div className="notif-when muted">
                    {new Date(n.at).toLocaleString('pt-BR', {
                      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                    })}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
